import React, { useMemo, useCallback } from 'react';
import Picker from '../picker';
import dayjs from 'dayjs';
import { generateIntArray } from '../../utils/generate-int-array';
import { withNativeProps } from '../../utils/native-props';
import { withDefaultProps } from '../../utils/with-default-props';
import { useNewControllableValue } from '../../utils/use-controllable-value';
var precisionRankRecord = {
  year: 0,
  month: 1,
  day: 2,
  hour: 3,
  minute: 4,
  second: 5
};
var thisYear = new Date().getFullYear();
var defaultProps = {
  min: new Date(new Date().setFullYear(thisYear - 10)),
  max: new Date(new Date().setFullYear(thisYear + 10)),
  precision: 'day'
};
export var DatePicker = withDefaultProps(defaultProps)(function (props) {
  var _a;

  var _useNewControllableVa = useNewControllableValue({
    value: props.value,
    defaultValue: (_a = props.defaultValue) !== null && _a !== void 0 ? _a : null,
    onChange: props.onConfirm
  }),
      value = _useNewControllableVa[0],
      setValue = _useNewControllableVa[1];

  function columns(selected) {
    var ret = [];
    var minYear = props.min.getFullYear();
    var minMonth = props.min.getMonth() + 1;
    var minDay = props.min.getDate();
    var minHour = props.min.getHours();
    var minMinute = props.min.getMinutes();
    var minSecond = props.min.getSeconds();
    var maxYear = props.max.getFullYear();
    var maxMonth = props.max.getMonth() + 1;
    var maxDay = props.max.getDate();
    var maxHour = props.max.getHours();
    var maxMinute = props.max.getMinutes();
    var maxSecond = props.max.getSeconds();
    var rank = precisionRankRecord[props.precision];

    if (rank >= precisionRankRecord.year) {
      var years = [];

      for (var i = minYear; i <= maxYear; i++) {
        years.push(i.toString());
      }

      ret.push(years);
    }

    var firstDayInSelectedMonth = dayjs(convertStringArrayToDate([selected[0], selected[1], '1']));
    var selectedYear = parseInt(selected[0]);
    var selectedMonth = parseInt(selected[1]);
    var selectedDay = parseInt(selected[2]);
    var selectedHour = parseInt(selected[3]);
    var selectedMinute = parseInt(selected[4]);
    var isInMinYear = selectedYear === minYear;
    var isInMaxYear = selectedYear === maxYear;
    var isInMinMonth = isInMinYear && selectedMonth === minMonth;
    var isInMaxMonth = isInMaxYear && selectedMonth === maxMonth;
    var isInMinDay = isInMinMonth && selectedDay === minDay;
    var isInMaxDay = isInMaxMonth && selectedDay === maxDay;
    var isInMinHour = isInMinDay && selectedHour === minHour;
    var isInMaxHour = isInMaxDay && selectedHour === maxHour;
    var isInMinMinute = isInMinHour && selectedMinute === minMinute;
    var isInMaxMinute = isInMaxHour && selectedMinute === maxMinute;

    if (rank >= precisionRankRecord.month) {
      var lower = isInMinYear ? minMonth : 1;
      var upper = isInMaxYear ? maxMonth : 12;
      var months = generateIntArray(lower, upper);
      ret.push(months.map(function (v) {
        return v.toString();
      }));
    }

    if (rank >= precisionRankRecord.day) {
      var _lower = isInMinMonth ? minDay : 1;

      var _upper = isInMaxMonth ? maxDay : firstDayInSelectedMonth.daysInMonth();

      var days = generateIntArray(_lower, _upper);
      ret.push(days.map(function (v) {
        return v.toString();
      }));
    }

    if (rank >= precisionRankRecord.hour) {
      var _lower2 = isInMinDay ? minHour : 0;

      var _upper2 = isInMaxDay ? maxHour : 23;

      var hours = generateIntArray(_lower2, _upper2);
      ret.push(hours.map(function (v) {
        return {
          label: ('0' + v.toString()).slice(-2),
          value: v.toString()
        };
      }));
    }

    if (rank >= precisionRankRecord.minute) {
      var _lower3 = isInMinHour ? minMinute : 0;

      var _upper3 = isInMaxHour ? maxMinute : 59;

      var minutes = generateIntArray(_lower3, _upper3);
      ret.push(minutes.map(function (v) {
        return {
          label: ('0' + v.toString()).slice(-2),
          value: v.toString()
        };
      }));
    }

    if (rank >= precisionRankRecord.second) {
      var _lower4 = isInMinMinute ? minSecond : 0;

      var _upper4 = isInMaxMinute ? maxSecond : 59;

      var seconds = generateIntArray(_lower4, _upper4);
      ret.push(seconds.map(function (v) {
        return {
          label: ('0' + v.toString()).slice(-2),
          value: v.toString()
        };
      }));
    }

    return ret;
  }

  var pickerValue = useMemo(function () {
    return convertDateToStringArray(value);
  }, [value]);
  var onConfirm = useCallback(function (val) {
    setValue(convertStringArrayToDate(val));
  }, [setValue]);
  var onSelect = useCallback(function (val) {
    var _a;

    var date = convertStringArrayToDate(val);

    if (date) {
      (_a = props.onSelect) === null || _a === void 0 ? void 0 : _a.call(props, date);
    }
  }, [props.onSelect]);
  return withNativeProps(props, /*#__PURE__*/React.createElement(Picker, {
    columns: columns,
    value: pickerValue,
    onCancel: props.onCancel,
    onClose: props.onClose,
    visible: props.visible,
    confirmText: props.confirmText,
    cancelText: props.cancelText,
    onConfirm: onConfirm,
    onSelect: onSelect,
    getContainer: props.getContainer,
    afterShow: props.afterShow,
    afterClose: props.afterClose,
    onClick: props.onClick
  }, function (items) {
    var _a;

    return (_a = props.children) === null || _a === void 0 ? void 0 : _a.call(props, convertStringArrayToDate(items.map(function (item) {
      return item === null || item === void 0 ? void 0 : item.value;
    })));
  }));
});

function convertDateToStringArray(date) {
  if (!date) return [];
  return [date.getFullYear().toString(), (date.getMonth() + 1).toString(), date.getDate().toString(), date.getHours().toString(), date.getMinutes().toString(), date.getSeconds().toString()];
}

function convertStringArrayToDate(value) {
  var _a, _b, _c, _d, _e, _f;

  if (value.length === 0) return null;
  var yearString = (_a = value[0]) !== null && _a !== void 0 ? _a : '1900';
  var monthString = (_b = value[1]) !== null && _b !== void 0 ? _b : '1';
  var dateString = (_c = value[2]) !== null && _c !== void 0 ? _c : '1';
  var hourString = (_d = value[3]) !== null && _d !== void 0 ? _d : '0';
  var minuteString = (_e = value[4]) !== null && _e !== void 0 ? _e : '0';
  var secondString = (_f = value[5]) !== null && _f !== void 0 ? _f : '0';
  return new Date(parseInt(yearString), parseInt(monthString) - 1, parseInt(dateString), parseInt(hourString), parseInt(minuteString), parseInt(secondString));
}